/* ============================================================
   Sticky add-to-cart bar (B7)
   Custom element paired with snippets/sticky-atc.liquid

   - Reveals itself when the main product CTA leaves the viewport
     upward (IntersectionObserver), hides it when the CTA is in view.
   - Subscribes to PUB_SUB_EVENTS.variantChange to keep price,
     "{color · size}" selection text, thumbnail and availability in sync.
   - Its CTA is a real <button form="{product_form_id}"> bound to the
     SAME main product-form, so there is exactly one add-to-cart path.

   Depends on globals provided by the theme: subscribe / PUB_SUB_EVENTS
   (constants.js + pubsub.js, loaded by the theme on product pages).
   ============================================================ */

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.ctaId = this.dataset.ctaId;
        this.observer = null;
        this.variantUnsubscriber = null;
        this.rafId = null;
        this.isVisible = false;
      }

      connectedCallback() {
        // Reparent to <body> so the bar lives at the document root, not inside
        // the product section. A position:fixed bar nested in the content flow
        // can be painted *under* later sections that Dawn's scroll-reveal
        // (`scroll-trigger animate--slide-in`) leaves with a lingering transform
        // — those become composited layers that, in Chrome, can render over a
        // fixed sibling regardless of z-index. As a direct, last child of
        // <body> the bar is painted after all content and always sits on top.
        // (Skip in the theme editor, where re-rendering the section would
        // otherwise orphan/duplicate the moved node.)
        if (
          !(window.Shopify && window.Shopify.designMode) &&
          this.parentElement !== document.body
        ) {
          // Moving the node re-fires connectedCallback with parent === body;
          // bail now and let that second pass run the real setup once.
          document.body.appendChild(this);
          return;
        }

        // The main buy-box CTA we watch to decide visibility.
        this.mainCta =
          document.getElementById(this.ctaId) ||
          document.querySelector('[data-main-atc]');

        // Cached refs to the parts we update on variant change.
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.selectionEl = this.querySelector('[data-sticky-selection]');
        this.addButton = this.querySelector('[data-sticky-add]');
        this.thumbEl = this.querySelector('.sticky-atc__thumb');

        // Element starts off-screen via CSS; make it part of the a11y tree now
        // that JS is wired up (so it can be revealed). It stays visually hidden
        // (translateY) until .is-visible is added.
        this.removeAttribute('hidden');

        this.setupObserver();
        this.setupVariantSync();
        this.setupHeaderFollow();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.headerObserver) this.headerObserver.disconnect();
        if (this.mobileMq && this._onMqChange) {
          if (this.mobileMq.removeEventListener) this.mobileMq.removeEventListener('change', this._onMqChange);
          else if (this.mobileMq.removeListener) this.mobileMq.removeListener(this._onMqChange);
        }
        if (typeof this.variantUnsubscriber === 'function') this.variantUnsubscriber();
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      /* ---- Follow the site header ---------------------------------------
         The site header (header.liquid's StickyHeader) reveals on scroll-up
         and hides on scroll-down by toggling .shopify-section-header-hidden on
         .section-header. This bar is fixed at top:var(--header-height); when the
         header hides, that leaves a gap above the bar. Mirror the header state
         onto the bar (.sticky-atc--header-hidden → top:0) so it tracks the
         header with a matching transition. */
      setupHeaderFollow() {
        this.siteHeader = document.querySelector('.section-header');
        if (!this.siteHeader || typeof MutationObserver === 'undefined') return;

        const sync = () => {
          this.classList.toggle(
            'sticky-atc--header-hidden',
            this.siteHeader.classList.contains('shopify-section-header-hidden')
          );
        };
        sync();
        this.headerObserver = new MutationObserver(sync);
        this.headerObserver.observe(this.siteHeader, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      /* ---- Visibility: IntersectionObserver on the main CTA ---- */
      setupObserver() {
        if (!this.mainCta || !('IntersectionObserver' in window)) return;

        // ≤749px the bar is a BOTTOM add-to-cart bar (see CSS) shown whenever the
        // main CTA is off-screen in EITHER direction — so it's present on first
        // load (buy-box CTA below the fold). Above that it's the top bar and only
        // appears once the CTA has scrolled past upward.
        this.mobileMq = window.matchMedia('(max-width: 749px)');
        this.lastEntry = null;

        this.observer = new IntersectionObserver(
          (entries) => {
            this.lastEntry = entries[entries.length - 1];
            this.evaluateVisibility();
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this.observer.observe(this.mainCta);

        // Re-evaluate when crossing the mobile/desktop breakpoint so the show
        // rule always matches the current layout.
        this._onMqChange = () => this.evaluateVisibility();
        if (this.mobileMq.addEventListener) {
          this.mobileMq.addEventListener('change', this._onMqChange);
        } else if (this.mobileMq.addListener) {
          this.mobileMq.addListener(this._onMqChange);
        }
      }

      evaluateVisibility() {
        const entry = this.lastEntry;
        if (!entry) return;
        let show;
        if (this.mobileMq && this.mobileMq.matches) {
          // Bottom bar: visible whenever the CTA is not on screen (above OR below).
          show = !entry.isIntersecting;
        } else {
          // Top bar: visible only once the CTA has scrolled past upward.
          show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
        this.toggle(show);
      }

      toggle(show) {
        if (show === this.isVisible) return;
        this.isVisible = show;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      /* ---- Variant sync: subscribe to variantChange pubsub ---- */
      setupVariantSync() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || data.sectionId !== this.sectionId) return;
          // Defer DOM writes to the next frame to avoid layout thrash while
          // product-info.js is also patching the buy box.
          if (this.rafId) cancelAnimationFrame(this.rafId);
          this.rafId = requestAnimationFrame(() => this.updateForVariant(data));
        });
      }

      updateForVariant({ variant, html }) {
        // 1) Price — reuse the freshly rendered price node from the variant
        //    change payload so sale/compare-at formatting matches the buy box.
        if (this.priceEl && html) {
          const freshPrice = html.getElementById(`price-${this.sectionId}`);
          if (freshPrice) this.priceEl.innerHTML = freshPrice.innerHTML;
        }

        // 2) Selection label "{option1 · option2 · …}"
        if (this.selectionEl && variant && Array.isArray(variant.options)) {
          this.selectionEl.textContent = variant.options.join(' · ');
        }

        // 3) Thumbnail — swap to the variant's featured image when available.
        if (this.thumbEl && variant) {
          const src = variant.featured_media
            ? variant.featured_media.preview_image && variant.featured_media.preview_image.src
            : variant.featured_image && variant.featured_image.src;
          if (src) {
            // Request a 120px-wide rendition for the 60px slot (retina).
            this.thumbEl.src = this.resizedUrl(src, 120);
            if (variant.featured_media && variant.featured_media.preview_image) {
              this.thumbEl.srcset = '';
            }
          }
        }

        // 4) Availability — mirror the main submit button's disabled state.
        if (this.addButton) {
          const unavailable = !variant || variant.available === false;
          this.addButton.toggleAttribute('disabled', unavailable);
        }
      }

      resizedUrl(src, width) {
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', width);
          return url.toString();
        } catch (e) {
          return src;
        }
      }
    }
  );
}
